import {ActionableList, ACTIONABLES, IActionable, IFocusActionables, IS_Actionable, IS_ActionableEnabled, OActionableListInit} from "/@back@/commons/actionables.js";
import {BaseElement, BASIS, isEltRefreshable, OSkinableInit} from "/@back@/commons/basis.js";
import {ActionBtn} from "/@back@/commons/widgets/buttons.js";
import {Action, ActionSeparator, IAction} from "/@lib@/commons/actions.js";
import {REG} from "/@lib@/commons/registry.js";
import {isRefreshHook} from "/@lib@/commons/utils/signboard.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";

/**
 * List actions, ie toolbar d'actions sans shadow-dom, sans gestion d'overflow.
 */
export interface Actionables<C> extends ActionableList<C> {
	initialize(init: OActionableListInit<C>): this;
}

export class Actionables<C> extends ActionableList<C> {
	protected _createButton(action: IAction<C>): Element {
		return ActionBtn.buildButton(action, this._actionContext, this.uiContext, this);
	}
}

customElements.define('c-actionables', Actionables);


/**
 * Toolbar simple d'actions.
 *
 * Attributes : cf ActionableList +
 * - Config init attribute : "ui-context" -> OBarActionsInit.uiContext
 * - class="vertical"
 */
export interface BarActions<C> extends ActionableList<C> {
	initialize(init: OBarActionsInit<C>): this;
}

export interface OBarActionsInit<C> extends OActionableListInit<C> {
	disableFullOverlay?: boolean
	overrideButtonsElts?: (button: Element) => void
}

export class BarActions<C> extends ActionableList<C> {

	get vertical(): boolean {return this.classList.contains("vertical")}

	overrideButtonsElts?: (button: Element) => void

	protected _initialize(init: OBarActionsInit<C>) {
		super._initialize(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		const ctn = sr.appendChild(<slot/>);
		this.addEventListener('keydown', this.onKeydown);
		this.overrideButtonsElts = init.overrideButtonsElts;
		if (init.disableFullOverlay) {
			this._initAndInstallSkin('c-bar-actions/disableFullOverlay', init);
		} else {
			ctn.addEventListener('mousedown', this.onMouseDown);
			ctn.addEventListener('mouseenter', this.onMouseEnter);
			ctn.addEventListener('mouseleave', this.onMouseLeave);
			this.addEventListener('focusin', this.onFocusIn);
			this.addEventListener('focusout', this.onFocusOut);
			this._initAndInstallSkin('c-bar-actions', init);
		}
	}

	protected _createButton(action: Action): Element {
		const btn = ActionBtn.buildButton(action, this._actionContext, this.uiContext, this);
		if (btn && this.overrideButtonsElts) this.overrideButtonsElts(btn);
		return btn;
	}

	protected evalShowFull(slot: HTMLSlotElement, ch = this.lastElementChild as HTMLElement) {
		if (this.vertical) {
			if (ch && this.offsetWidth < ch.offsetLeft + ch.offsetWidth - 2) DOM.addClass(slot, 'show');
		} else {
			if (ch && this.offsetHeight < ch.offsetTop + ch.offsetHeight - 2) DOM.addClass(slot, 'show');
		}
	}

	protected onMouseDown(this: HTMLSlotElement, ev: MouseEvent) {
		if (this === ev.target) {
			//Evite de perdre le focus de la toolbar si click à coté d'un bouton.
			ev.stopImmediatePropagation();
			ev.preventDefault();
		}
	}

	protected onMouseLeave(this: HTMLSlotElement) {
		//Si le focus est dans notre descendance logique, on garde la barre étendue: C'est le blur qui la refermera.
		if (DOMSH.isLogicalFlatAncestor(DOMSH.findHost(this), DOMSH.findDeepActiveElement())) return;
		DOM.removeClass(this, 'show');
	}

	protected onMouseEnter(this: HTMLSlotElement) {
		(DOMSH.findHost(this) as BarActions<C>).evalShowFull(this);
	}

	protected onFocusIn(ev: FocusEvent) {
		this.evalShowFull(this.shadowRoot.querySelector('slot') as HTMLSlotElement /*, ev.target as HTMLElement on passe en mode 'show' quelquesoit le btn fosusé. */);
	}

	protected onFocusOut(ev: FocusEvent) {
		if (ev.relatedTarget && DOMSH.isLogicalFlatAncestor(this, ev.relatedTarget as Node)) {
			//Focus dans un widget interne à cette barre, on transfert l'écoute du blur.
			const blur = (ev: FocusEvent) => {
				ev.target.removeEventListener('blur', blur);
				this.onFocusOut(ev);
			};
			ev.relatedTarget.addEventListener('blur', blur);
			return;
		}
		DOM.removeClass(this.shadowRoot.querySelector('slot'), 'show');
	}

	protected onKeydown(ev: KeyboardEvent) {
		if (this.vertical) {
			switch (ev.key) {
			case 'ArrowUp':
				this.moveFocus(ev, false);
				break;
			case 'ArrowDown':
				this.moveFocus(ev, true);
				break;
			}
		} else {
			//TODO  RTL ?
			switch (ev.key) {
			case 'ArrowLeft':
				this.moveFocus(ev, false);
				break;
			case 'ArrowRight':
				this.moveFocus(ev, true);
				break;
			}
		}
	}

	protected moveFocus(ev: KeyboardEvent, forward: boolean) {
		const target = ev.composedPath()[0] as Node;
		if (target.parentNode === this) {
			const next = forward ? DOM.findNextSibling(target, IS_ActionableEnabled) : DOM.findPreviousSibling(target, IS_ActionableEnabled);
			if (next) {
				ev.stopImmediatePropagation();
				next.focus();
			}
		}
	}

}

REG.reg.registerSkin('c-bar-actions', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: calc(6px + var(--icon-size));
		min-width: 0;
		align-items: center;
		position: relative;
	}

	:host(.vertical) {
		min-width: calc(6px + var(--icon-size));
		min-height: 0;
		flex-direction: column;
	}

	slot {
		position: relative;
	  overflow: hidden;
		display: flex;
		min-height: 0;
		min-width: 0;
		max-height: calc(6px + var(--icon-size));
		flex-wrap: wrap;
		flex: 1;
	}

	:host(.vertical) > slot {
	  flex-direction: column;
	  max-height: unset;
	  max-width: calc(6px + var(--icon-size));
  }

  slot.show {
	  position: absolute;
	  top: 0;
	  left: 0;
	  overflow: visible;
	  bottom: unset;
	  z-index: 100;
	  max-height: unset;
	  background-color: var(--bgcolor); /* on doit écraser ce qui est en dessous. */
	  border: 1px solid var(--border-color);
	  box-shadow: 1px 1px 6px var(--fade-color);
  }

  ::slotted(hr) {
	  border-top: 1px solid var(--border-color);
	  border-inline-start: 1px solid var(--border-color);
	  border-inline-end: none;
	  border-bottom: none;
	  margin: .2rem;
	  align-self: stretch;
  }
`);


REG.reg.registerSkin('c-bar-actions/disableFullOverlay', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: calc(6px + var(--icon-size));
		min-width: 0;
		flex-wrap: wrap;
		align-items: center;
	}

	:host(.vertical) {
		flex-direction: column;
		min-height: 0;
		min-width: calc(6px + var(--icon-size));
	}

	::slotted(hr) {
		border-top: 1px solid var(--border-color);
		border-inline-start: 1px solid var(--border-color);
		border-inline-end: none;
		border-bottom: none;
		margin: .2em;
		align-self: stretch;
	}
`);

customElements.define('c-bar-actions', BarActions);


/**
 * Barre d'outils partagée comportant :
 * - une liste d'actions "fixes" au début,
 * - une liste d'actions contextuelles liées au focus courant. Placés à la suite de la 1ère liste,
 *    les dernières actions sont masquées par défaut en cas d'espace insuffisant.
 * - une liste d'actions "fixes" à la fin, utilisés pour mettre un bouton d'otions et/ou un bouton "close". Ces
 *    actions sont prioritaires sur l'affichage des 2 précédentes listes.
 *
 * La liste d'actions contextuelles est obtenue via l'écoute d'events DOM sur un ou plusieurs autres éléments DOM.
 * ATTENTION : La BarShared DOIT se trouver dans le même arbre DOM que le(s) noeud(s) écouté(s) (ie pas dans un shadow).
 * Trois events sont écoutés :
 * - 'focusin' : partant du target de l'event, un élément ancêtre fournissant une liste de boutons via OFocusActionables est recherché.
 * - 'c-focus-actions' : CustomEvent permetant de forcer un refresh des focus-actions de la barre. CustomEvent.detail doit être un OFocusActionables
 *      ou null pour forcer un effacement.
 * - 'focusout' : reset les focus-actions jusqu'au prochain 'focusin' ou 'c-focus-actions'.
 *
 * Attributes : cf BaseElement +
 * Config init attributes: start-actions, end-actions, action-context
 */
export interface BarShared<C> extends BaseElement {
	initialize(init?: OBarSharedInit<C>): this
}

export interface OBarSharedInit<C> extends OSkinableInit {
	/** List des actions fixes au début. */
	startActions?: Action<C>[]

	/** List des actions fixes en fin de toolbar. */
	endActions?: Action<C>[]

	/** ActionContext pour les actions fixes en début et fin. */
	actionContext?: C

	/** Noeuds racines sur lesquels écouter les events 'focusin', 'focusout' et 'c-focus-actions'. */
	focusListening?: HTMLElement | HTMLElement[]
}

export class BarShared<C> extends BaseElement {
	startActions: Action<C>[];

	endActions: Action<C>[];

	actionContext: C;

	focusListening?: HTMLElement | HTMLElement[];

	/** Boutons à afficher au focus. */
	focusActionables: IActionable<any>[];

	protected _startBar: HTMLElement;
	protected _endBar: HTMLElement;

	protected _initialize(init: OBarSharedInit<C>) {
		this.actionContext = init.actionContext;
		if (init.startActions) this.startActions = init.startActions;
		if (init.endActions) this.endActions = init.endActions;
		if (init.focusListening) this.focusListening = init.focusListening;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this._startBar = sr.appendChild(<div id="start"/>);
		if (this.endActions && this.endActions.length > 0) this._createEndBar();

		this._startBar.addEventListener('mousedown', this.onMouseDown);
		this._startBar.addEventListener('mouseenter', this.onMouseEnter);
		this._startBar.addEventListener('mouseleave', this.onMouseLeave);
		this._startBar.addEventListener('focusin', this.onFocusIn);
		this._startBar.addEventListener('focusout', this.onFocusOut);

		this._requestIdleBinded = this._requestIdle.bind(this);
	}

	buildInitFromAtts(init?: OBarSharedInit<C>): OBarSharedInit<C> {
		init = super.buildInitFromAtts(init);
		const reg = this.findReg(init);
		if (this.hasAttribute('start-actions')) init.startActions = reg.getList(BASIS.extractAttr(this, 'start-actions'));
		if (this.hasAttribute('end-actions')) init.endActions = reg.getList(BASIS.extractAttr(this, 'end-actions'));
		if (this.hasAttribute('action-context')) init.actionContext = reg.getSvc(BASIS.extractAttr(this, 'action-context'));
		return init;
	}

	connectedCallback() {
		super.connectedCallback();
		if (this.focusListening) {
			function add(elt: HTMLElement, lstn: (this: BarShared<C>, ev: CustomEvent) => void) {
				elt.addEventListener('focusin', lstn);
				elt.addEventListener('c-focus-actions', lstn);
				elt.addEventListener('focusout', lstn);
			}

			this._onEventsBinded = this._onEvents.bind(this);
			if (Array.isArray(this.focusListening)) for (const elt of this.focusListening) add(elt, this._onEventsBinded);
			else add(this.focusListening, this._onEventsBinded);
		}
	}

	disconnectedCallback() {
		if (this.focusListening) {
			function rem(elt: HTMLElement, lstn: (this: BarShared<C>, ev: CustomEvent) => void) {
				elt.removeEventListener('focusin', lstn);
				elt.removeEventListener('c-focus-actions', lstn);
				elt.removeEventListener('focusout', lstn);
			}

			if (Array.isArray(this.focusListening)) for (const elt of this.focusListening) rem(elt, this._onEventsBinded);
			else rem(this.focusListening, this._onEventsBinded);
			this._onEventsBinded = null;
		}
	}

	planRefresh() {
		if (!this._refreshPlanned) this._refreshPlanned = window.requestIdleCallback(this._requestIdleBinded);
	}

	protected _onEventsBinded: (this: BarShared<C>, ev: CustomEvent) => void;

	protected _onEvents(ev: CustomEvent | FocusEvent) {
		if (ev instanceof CustomEvent) {
			//event c-focus-actions
			this.focusActionables = ev.detail ? (ev.detail as IFocusActionables<any>).focusActionables : null;
			this.planRefresh();
		} else if (ev.type === 'focusin') {
			for (const node of ev.composedPath()) {
				if ('focusActionables' in node) {
					this.focusActionables = (node as IFocusActionables<any>).focusActionables;
					this.planRefresh();
					return;
				} else if (node === this) {
					if (this.focusActionables) {
						this.focusActionables = null;
						this.planRefresh();
					}
					return;
				}
			}
		} else if (ev.type === 'focusout') {
			if (ev.relatedTarget && DOMSH.isLogicalFlatAncestor(this, ev.relatedTarget as Node)) return;
			this.focusActionables = null;
			this.planRefresh();
		}
	}

	/** gestion des cycles de refresh. */
	protected _refreshPlanned: number;
	protected _requestIdleBinded: (deadline: RequestIdleDeadline) => void;

	protected _requestIdle(this: BarShared<C>, deadline: RequestIdleDeadline) {
		if (deadline.timeRemaining() < 5) {
			this._refreshPlanned = window.requestIdleCallback(this._requestIdleBinded);
			return;
		}
		this._refreshPlanned = null;
		this.refresh();
	}

	protected evalShowFull(ch = this._startBar.lastElementChild as HTMLElement) {
		if (ch && this.offsetHeight < ch.offsetTop + ch.offsetHeight) DOM.addClass(this._startBar, 'show');
	}

	protected onMouseDown(this: HTMLElement, ev: MouseEvent) {
		if (this === ev.target) {
			//Evite de perdre le focus de la toolbar si click à coté d'un bouton.
			ev.stopImmediatePropagation();
			ev.preventDefault();
		}
	}

	protected onMouseLeave(this: HTMLElement) {
		//Si le focus est dans notre descendance logique, on garde la barre étendue: C'est le blur qui la refermera.
		if (DOMSH.isLogicalFlatAncestor(DOMSH.findHost(this), DOMSH.findDeepActiveElement())) return;
		DOM.removeClass(this, 'show');
	}

	protected onMouseEnter(this: HTMLElement) {
		(DOMSH.findHost(this) as BarShared<C>).evalShowFull();
	}

	protected onFocusIn(this: HTMLElement, ev: FocusEvent) {
		(DOMSH.findHost(this) as BarShared<C>).evalShowFull(/*ev.target as HTMLElement on passe en mode 'show' quelquesoit le btn fosusé. */);
	}

	protected onFocusOut(this: HTMLElement, ev: FocusEvent) {
		const barShared = this instanceof BarShared ? this : DOMSH.findHost(this) as BarShared<C>;
		if (ev.relatedTarget && DOMSH.isLogicalFlatAncestor(barShared._startBar, ev.relatedTarget as Node)) {
			//Focus dans un widget interne à cette barre, on transfert l'écoute du blur.
			const blur = (ev: FocusEvent) => {
				ev.target.removeEventListener('blur', blur);
				barShared.onFocusOut(ev);
			};
			ev.relatedTarget.addEventListener('blur', blur);
			return;
		}
		DOM.removeClass(barShared._startBar, 'show');
		barShared.focusActionables = null;
		barShared.planRefresh();
	}

	protected _refresh() {
		if (!this.actionContext) {
			this.textContent = null;
			return;
		}
		if (isRefreshHook(this.actionContext) && this.actionContext.onRefreshCycle(true) === 'stop') return;
		try {
			//this.textContent = null; //désactivation refresh par diff

			// fixed start actions.
			let nextTag = this._startBar.firstElementChild;
			if (this.startActions) for (let i = 0; i < this.startActions.length; i++) {
				const action = this.startActions[i];
				if (IS_Actionable(nextTag) && nextTag.action === action) {
					if (isEltRefreshable(nextTag)) nextTag.refresh();
					nextTag = nextTag.nextElementSibling;
				} else {
					const b = this._createButton(action);
					if (b) this._startBar.insertBefore(b, nextTag);
				}
			}

			// focus actionables.
			if (this.focusActionables && this.focusActionables.length > 0) {
				//Separator
				if (IS_Actionable(nextTag) && nextTag.action instanceof ActionSeparator) {
					if (isEltRefreshable(nextTag)) nextTag.refresh();
					nextTag = nextTag.nextElementSibling;
				} else {
					this._startBar.insertBefore(this._createButton(new ActionSeparator()), nextTag);
				}
				// focus actionables.
				for (let i = 0; i < this.focusActionables.length; i++) {
					const actionable = this.focusActionables[i];
					if (IS_Actionable(nextTag) && nextTag.action === actionable.action) {
						if (isEltRefreshable(nextTag)) nextTag.refresh();
						nextTag = nextTag.nextElementSibling;
					} else {
						this._startBar.insertBefore(actionable, nextTag);
					}
				}
			}

			// cleanup startBar
			while (nextTag) {
				const toRem = nextTag;
				nextTag = nextTag.nextElementSibling;
				toRem.remove();
			}
			ACTIONABLES.cleanupDoubleSep(this._startBar);

			// fixed end actions.
			if (this.endActions && this.endActions.length > 0) {
				if (!this._endBar) this._createEndBar();
				nextTag = this._endBar.firstElementChild;
				for (let i = 0; i < this.endActions.length; i++) {
					const action = this.endActions[i];
					if (IS_Actionable(nextTag) && nextTag.action === action) {
						if (isEltRefreshable(nextTag)) nextTag.refresh();
						nextTag = nextTag.nextElementSibling;
					} else {
						const b = this._createButton(action);
						if (b) this._endBar.insertBefore(b, nextTag);
					}
				}

				// cleanup endBar
				while (nextTag) {
					const toRem = nextTag;
					nextTag = nextTag.nextElementSibling;
					toRem.remove();
				}
				ACTIONABLES.cleanupDoubleSep(this._endBar);
			} else if (this._endBar) this._endBar.textContent = null;

		} finally {
			if (isRefreshHook(this.actionContext)) this.actionContext.onRefreshCycle(false);
		}
	}

	protected _createEndBar(): HTMLElement {
		this.shadowRoot.appendChild(<hr/>);
		return (this._endBar = this.shadowRoot.appendChild(<div id="end"/>));
	}

	protected _createButton(action: Action): Element {
		return ActionBtn.buildButton(action, this.actionContext, 'bar', this);
	}
}

REG.reg.registerSkin('c-bar-shared', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 1.7em;
		min-width: 0;
		flex: 1;
		align-items: center;
		justify-content: flex-end;
		position: relative;
  }

  :host([vertical]) {
	  flex-direction: column;
	  min-height: 0;
	  min-width: 1.7em;
	  justify-content: unset;
  }

  #end {
	  display: flex;
	  min-height: 0;
	  min-width: 0;
  }

  #start {
	  position: relative;
	  overflow: hidden;
	  display: flex;
	  min-width: 0;
	  min-height: calc(6px + var(--icon-size));
	  flex-wrap: wrap;
	  flex: 1;
  }

  :host([vertical]) #start {
	  flex-direction: column;
	  min-height: unset;
	  min-width: calc(6px + var(--icon-size));
  }

  #start.show {
	  position: absolute;
	  top: 0;
	  left: 0;
	  overflow: visible;
	  bottom: unset;
	  z-index: 100;
	  max-height: unset;
	  border: 1px solid var(--border-color);
	  box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.5);
	  background-color: var(--bgcolor);
  }

  hr {
	  border-top: 1px solid var(--border-color);
	  border-inline-start: 1px solid var(--border-color);
	  border-inline-end: none;
	  border-bottom: none;
	  margin: .2em;
	  align-self: stretch;
  }
`);

customElements.define('c-bar-shared', BarShared);


/**
 * Barre d'information.
 */
export class BarStatus extends BaseElement {

	protected _textCtn: HTMLElement;

	protected _initialize(init: OSkinableInit) {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this._textCtn = sr.appendChild(document.createElement('div'));
	}

	setStatus(status: string) {
		this._textCtn.textContent = status;
	}
}

REG.reg.registerSkin('c-bar-status', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1;
		padding: 2px;
	}

	div {
		flex: 1;
		justify-content: start;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		font-size: small;
	}
`);

customElements.define('c-bar-status', BarStatus);
